import type {
  Account,
  ApiKey,
  InstallToken,
  RechargeOption,
  RechargeOrder,
  UsageResponse,
  UserPublic,
} from "./types";

const API_PREFIX = "/api/web";

export interface ApiOptions extends Omit<RequestInit, "body"> {
  body?: BodyInit | null;
}

async function apiJSON<T = unknown>(path: string, opts: ApiOptions = {}): Promise<T> {
  const headers = new Headers(opts.headers);
  if (!headers.has("Content-Type")) headers.set("Content-Type", "application/json");

  const resp = await fetch(API_PREFIX + path, { ...opts, headers });
  const raw = await resp.text();

  // 502 / 504 / proxy outages may return HTML or empty bodies. Strict
  // JSON.parse turns those into a SyntaxError; swallow parse errors so
  // the HTTP status surfaces to the caller instead.
  let data: Record<string, unknown> = {};
  if (raw) {
    try {
      data = JSON.parse(raw);
    } catch {
      data = {};
    }
  }
  if (!resp.ok) {
    const msg =
      (typeof data.error === "string" && data.error) ||
      (typeof data.message === "string" && data.message) ||
      `请求失败 (HTTP ${resp.status})`;
    throw new Error(msg);
  }
  return data as T;
}

export function authHeaders(session: string): Record<string, string> {
  return { Authorization: `Bearer ${session}` };
}

// --- v3 session-aware login / register ---

export interface LoginResponse {
  session_token: string;
  expires_at: string;
  user: UserPublic;
}

export type LoginInput =
  | { method: "api_key"; api_key: string }
  | { method: "password"; username: string; password: string };

export function login(input: LoginInput): Promise<LoginResponse> {
  return apiJSON<LoginResponse>("/login", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export interface RegisterInput {
  email: string;
  password: string;
  username?: string;
}

export function register(input: RegisterInput): Promise<LoginResponse> {
  return apiJSON<LoginResponse>("/register", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function logout(session: string): Promise<void> {
  return apiJSON<void>("/logout", { method: "POST", headers: authHeaders(session) });
}

export interface MeResponse {
  user: UserPublic;
  updated_at: string;
}

export function fetchMe(session: string): Promise<MeResponse> {
  return apiJSON<MeResponse>("/me", { method: "GET", headers: authHeaders(session) });
}

export function updateProfile(
  session: string,
  payload: { username?: string; display_name?: string; email?: string },
): Promise<{ user: UserPublic }> {
  return apiJSON<{ user: UserPublic }>("/me/profile", {
    method: "PUT",
    headers: authHeaders(session),
    body: JSON.stringify(payload),
  });
}

export function changePassword(
  session: string,
  oldPassword: string,
  newPassword: string,
): Promise<void> {
  return apiJSON<void>("/me/password", {
    method: "PUT",
    headers: authHeaders(session),
    body: JSON.stringify({ old_password: oldPassword, new_password: newPassword }),
  });
}

// --- v3 api-key (one user, one key) ---

export interface ApiKeyResponse {
  api_key: ApiKey;
}

export function fetchApiKey(session: string): Promise<ApiKeyResponse> {
  return apiJSON<ApiKeyResponse>("/me/api_key", { method: "GET", headers: authHeaders(session) });
}

export function revokeApiKey(session: string): Promise<ApiKeyResponse> {
  return apiJSON<ApiKeyResponse>("/me/api_key/revoke", {
    method: "POST",
    headers: authHeaders(session),
  });
}

// --- usage dashboard ---

export interface UsageQuery {
  start: number;   // unix seconds
  end: number;     // unix seconds
  bucket?: "hour" | "day" | "week" | "month";
}

export function fetchUsage(session: string, q: UsageQuery): Promise<UsageResponse> {
  const params = new URLSearchParams();
  params.set("start", String(q.start));
  params.set("end", String(q.end));
  if (q.bucket) params.set("bucket", q.bucket);
  return apiJSON<UsageResponse>(`/usage?${params.toString()}`, {
    method: "GET",
    headers: authHeaders(session),
  });
}

// --- install tokens (授权码) ---

export interface ListTokensResponse {
  tokens: InstallToken[];
}

export function listTokens(session: string): Promise<ListTokensResponse> {
  return apiJSON<ListTokensResponse>("/tokens", {
    method: "GET",
    headers: authHeaders(session),
  });
}

export interface CreateTokenInput {
  agent_type: string;
  expires?: string;
}

export interface CreateTokenResponse {
  token: InstallToken;
  signed_token: string;
  api_key: ApiKey;
}

export function createToken(session: string, input: CreateTokenInput): Promise<CreateTokenResponse> {
  return apiJSON<CreateTokenResponse>("/tokens", {
    method: "POST",
    headers: authHeaders(session),
    body: JSON.stringify(input),
  });
}

export function revokeToken(session: string, tokenID: string): Promise<void> {
  return apiJSON<void>(`/tokens/${encodeURIComponent(tokenID)}/revoke`, {
    method: "POST",
    headers: authHeaders(session),
  });
}

// --- account (legacy v1 shape, used by topbar key preview) ---

export function fetchAccount(session: string): Promise<Account> {
  return apiJSON<Account>("/account", { method: "GET", headers: authHeaders(session) });
}

// --- recharge ---

export function fetchRechargeOptions(): Promise<{ options: RechargeOption[] }> {
  return apiJSON<{ options: RechargeOption[] }>("/recharge/options", { method: "GET" });
}

export function fetchRechargeOptionsV2(session: string): Promise<{ options: RechargeOption[] }> {
  return apiJSON<{ options: RechargeOption[] }>("/recharge/options/v2", {
    method: "GET",
    headers: authHeaders(session),
  });
}

export interface CreateOrderInput {
  amount_cny?: number;
  plan_id?: string;
}

export function createRechargeOrder(
  session: string,
  input: CreateOrderInput,
): Promise<RechargeOrder> {
  return apiJSON<RechargeOrder>("/recharge/orders", {
    method: "POST",
    headers: authHeaders(session),
    body: JSON.stringify(input),
  });
}

export function listRechargeOrders(session: string): Promise<{ orders: RechargeOrder[] }> {
  return apiJSON<{ orders: RechargeOrder[] }>("/recharge/orders", {
    method: "GET",
    headers: authHeaders(session),
  });
}

// --- error message translation ---

export function loginErrorText(err: unknown): string {
  const message = err instanceof Error ? err.message : "";
  if (message.includes("api key validation failed")) {
    return "API Key 校验失败，请确认填写的是完整 Key，不是 apis.agentfly.net。";
  }
  if (message.includes("invalid credentials")) {
    return "用户名或密码错误。";
  }
  if (message.includes("too many login")) {
    return "登录太频繁，请稍后再试。";
  }
  return message || "登录失败";
}
